import { useEffect, memo, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { manageAppsAPI } from 'services/manageApps.service';
import { Loader } from 'components';
import {
  generalActions,
  formErrorsCampaign_action,
  targetingPlacementsActions,
  creativesActions,
} from 'store/actions';
import { appNumberToPromote } from 'store/selectors';
import { campaignEnvironment } from 'constants';
import ChooseAppOrWebsite from './components/ChooseAppOrWebsite';

function ApplicationSection({ environment }) {
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(location.search);
  const appFromUrl = queryParams.get('app');

  const { data: appList, isFetching } = manageAppsAPI.useFetchAppsToPromoteByFilterQuery(false);
  const { data: websiteList, isFetchingWeb } =
    manageAppsAPI.useFetchAppsToPromoteByFilterQuery(true);

  const currentAppNumberToPromote = useSelector(appNumberToPromote);
  const prevAppRef = useRef(currentAppNumberToPromote);
  const errorFlag = useSelector((state) => state.formErrorsCampaign.main);

  const appToPromote = useMemo(
    () => appList?.find((app) => app?.pba_id === currentAppNumberToPromote),
    [currentAppNumberToPromote, appList],
  );
  const websiteToPromote = useMemo(
    () => websiteList?.find((app) => app?.pba_id === currentAppNumberToPromote),
    [currentAppNumberToPromote, websiteList],
  );

  // fallback data that comes from db (even is app is archived)
  const selectedAppFromBase = useSelector(
    (state) => state.general?.mainCampaignObject?.campaignPageExtendedDetails?.promotedApp,
  );
  const isAppFromBaseWebsite = useSelector(
    (state) => state.general?.mainCampaignObject?.currentCampaignObj?.isWebsite,
  );
  const isArchivedApp = !appToPromote && !websiteToPromote && !!selectedAppFromBase?.pba_id;
  const hideArchivedApp = isArchivedApp && isAppFromBaseWebsite;
  const hideArchivedWebsite = isArchivedApp && !isAppFromBaseWebsite;

  useEffect(() => {
    if (isArchivedApp) return;

    dispatch(
      generalActions.updateCurrentCampaignObj({
        propField: 'isWebsite',
        newValue: !!websiteToPromote,
      }),
    );
  }, [!!websiteToPromote, !!currentAppNumberToPromote]);

  useEffect(() => {
    prevAppRef.current = !!currentAppNumberToPromote;
  }, [!!currentAppNumberToPromote]);

  const handleChooseNewApp = useCallback(
    (data) => {
      dispatch(
        generalActions.updateCurrentCampaignObj({
          propField: 'app',
          newValue: data?.data?.apps?.appId,
        }),
      );
      dispatch(targetingPlacementsActions.clearState());
      dispatch(formErrorsCampaign_action.resetErrors());
      dispatch(generalActions.resetCurrentCampaignObject());
      dispatch(creativesActions.resetTrackingAndCreative());
    },
    [errorFlag],
  );

  const canChange = useMemo(() => {
    if (environment === campaignEnvironment.edit) return false;
    if (environment == campaignEnvironment.duplicate) return false;
    if (environment === campaignEnvironment.new_empty && appFromUrl !== null) return false;
    return true;
  }, [environment, appFromUrl]);

  if (isFetching || isFetchingWeb)
    return (
      <div style={{ paddingBottom: '40px' }}>
        <Loader.Local delay={0} />
      </div>
    );

  return (
    <>
      {(!websiteToPromote || canChange) && !hideArchivedApp && (
        <ChooseAppOrWebsite
          title='App to promote:'
          errorMessage='Please choose App'
          isWebsite={false}
          environment={environment}
          canChange={canChange}
          errorFlag={errorFlag}
          appToPromote={appToPromote}
          handleChooseApp={handleChooseNewApp}
          selectedAppFromBase={selectedAppFromBase}
        />
      )}
      {(!appToPromote || canChange) && !hideArchivedWebsite && (
        <div style={{ paddingTop: '24px' }}>
          <ChooseAppOrWebsite
            title='Web campaign:'
            errorMessage='Please choose Website'
            isWebsite={true}
            environment={environment}
            canChange={canChange}
            errorFlag={errorFlag}
            appToPromote={websiteToPromote}
            handleChooseApp={handleChooseNewApp}
            selectedAppFromBase={selectedAppFromBase}
          />
        </div>
      )}
    </>
  );
}

export default memo(ApplicationSection);
